import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/features/auth/AuthContext';
import { useFastTrackConfig, type FastTrackMode } from '@/features/dashboard/useFastTrackConfig';
import { startOfWeek, differenceInWeeks, parseISO } from 'date-fns';

interface CohortInfo {
  cohort: number | null;
  currentWeek: number; // 1-5, the curriculum week for this cohort
  cohortCallLink: string;
  cohortCallTime: string;
  allCohorts: { cohort: number; time: string; link: string; currentWeek: number }[];
  switchCohort: (n: number) => Promise<void>;
  /** Global Fast Track delivery mode. Cohort fields are meaningless when 'recorded'. */
  mode: FastTrackMode;
  /** Vimeo player URLs for the five recorded sessions. Index = week - 1. */
  recordings: string[];
  loading: boolean;
}

/**
 * Returns which curriculum week a given cohort is on, given weeks since anchor.
 * cohort is 1-indexed (1-5).
 */
function getCohortWeek(weeksSinceAnchor: number, cohort: number): number {
  return ((weeksSinceAnchor + cohort - 1) % 5) + 1;
}

/** Returns which cohort (1-5) is currently in curriculum Week 1. */
function getCohortInWeek1(weeksSinceAnchor: number): number {
  // Solve: ((weeksSinceAnchor + C - 1) % 5) + 1 === 1
  // => (weeksSinceAnchor + C - 1) % 5 === 0
  // => C = (5 - (weeksSinceAnchor % 5)) % 5 + 1... let's just iterate
  for (let c = 1; c <= 5; c++) {
    if (getCohortWeek(weeksSinceAnchor, c) === 1) return c;
  }
  return 1;
}

export function useCohort(): CohortInfo {
  const { session } = useAuth();
  const config = useFastTrackConfig();
  const [cohort, setCohort] = useState<number | null>(null);
  const [cohortLoading, setCohortLoading] = useState(true);

  const { mode, anchorDate, links, times, recordings, loading: configLoading } = config;

  const weeksSinceAnchor = (() => {
    if (!anchorDate) return 0;
    const anchor = startOfWeek(parseISO(anchorDate), { weekStartsOn: 1 });
    const now = startOfWeek(new Date(), { weekStartsOn: 1 });
    return Math.max(0, differenceInWeeks(now, anchor));
  })();

  // Load the member's cohort, auto-assigning one if they don't have it yet.
  useEffect(() => {
    if (!session?.user || configLoading) return;
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from('member_progress')
        .select('cohort')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (cancelled) return;

      const existingCohort = (data as { cohort: number | null } | null)?.cohort ?? null;

      if (existingCohort) {
        setCohort(existingCohort);
      } else if (anchorDate && mode === 'live') {
        // Auto-assign to whichever cohort is in Week 1.
        // Skipped in recorded mode: there are no calls to attend, so a
        // date-rotated cohort would be meaningless. Members who already have a
        // cohort keep it, so flipping back to live is lossless.
        const assignedCohort = getCohortInWeek1(weeksSinceAnchor);
        setCohort(assignedCohort);
        await supabase
          .from('member_progress')
          .upsert({ user_id: session.user.id, cohort: assignedCohort }, { onConflict: 'user_id' });
      }

      setCohortLoading(false);
    };

    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, configLoading, mode, anchorDate]);

  const switchCohort = useCallback(async (n: number) => {
    if (!session?.user || n < 1 || n > 5) return;
    setCohort(n);
    await supabase
      .from('member_progress')
      .update({ cohort: n })
      .eq('user_id', session.user.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  const currentWeek = cohort ? getCohortWeek(weeksSinceAnchor, cohort) : 1;
  const cohortCallLink = cohort ? links[cohort - 1] ?? '' : '';
  const cohortCallTime = cohort ? times[cohort - 1] ?? '' : '';

  const allCohorts = times.map((time, i) => ({
    cohort: i + 1,
    time,
    link: links[i] ?? '',
    currentWeek: getCohortWeek(weeksSinceAnchor, i + 1),
  }));

  return {
    cohort,
    currentWeek,
    cohortCallLink,
    cohortCallTime,
    allCohorts,
    switchCohort,
    mode,
    recordings,
    loading: configLoading || cohortLoading,
  };
}
